<template>
    <!--
        Менеджер вкладок таблиці тікетів.

        Було: один суцільний список — селект типу, селект значення, окрема модалка
        кольору (з перейменуванням через dblclick), окремий «мульти-фільтр» з власною
        кнопкою збереження. Незрозуміло, що є чернеткою, а що вже збереженою вкладкою.

        Стало: дві зони — ліворуч список вкладок (порядок, видимість, редагування,
        видалення), праворуч конструктор однієї вкладки (назва → колір → умови).
        Одна вкладка з однією умовою і вкладка з кількома умовами створюються тим
        самим шляхом: умови просто додаються рядками.

        Працюємо з локальною КОПІЄЮ табів, тому «Скасувати» реально скасовує:
        раніше модалка мутувала пропс, і зміни лишались навіть після закриття
        хрестиком. Новий склад табів віддаємо батькові подією changeTabs.
    -->
    <dialogBox
        @close="this.$emit('close')"
    >
      <template v-slot:title>{{ $t('tabsSettings') }}</template>
      <template v-slot:body>
        <div class="tabsmgr">
            <div class="tabsmgr__grid">

                <!-- ЛІВА ЗОНА — уже створені вкладки -->
                <section class="tabsmgr__panel">
                    <div class="tabsmgr__panel-head">
                        <div class="tabsmgr__panel-title">
                            {{ $t('myTabs') }}
                            <span class="tabsmgr__count">{{ localTabs.length }}</span>
                        </div>
                        <button
                            v-if="localTabs.length > 0"
                            type="button"
                            class="btn btn-sm btn-soft-success tabsmgr__new-btn"
                            @click="resetDraft()"
                        >
                            <i class="bx bx-plus"></i> {{ $t('newTab') }}
                        </button>
                    </div>

                    <!-- Порожній стан замість німого списку: одразу видно, що робити -->
                    <div v-if="localTabs.length === 0" class="tabsmgr__empty">
                        <i class="bx bx-collection"></i>
                        <div class="tabsmgr__empty-text">{{ $t('noTabsHint') }}</div>
                    </div>

                    <template v-else>
                        <!--
                            handle=".tabsmgr__drag" — тягнути можна лише за ручку,
                            інакше клік по рядку (редагування) конфліктує з drag.
                        -->
                        <draggable
                            v-model="localTabs"
                            handle=".tabsmgr__drag"
                            class="tabsmgr__list"
                        >
                            <div
                                v-for="(item, index) in localTabs"
                                :key="item.tab + '_' + index"
                                class="tabsmgr__row"
                                :class="{ 'is-editing': editIndex === index, 'is-off': !item.checked }"
                            >
                                <i class="ri-drag-move-2-line tabsmgr__drag" :title="$t('dragToReorder')"></i>

                                <!-- Крапка кольору, а не заливка: так само таб виглядає і в таблиці -->
                                <span class="tabsmgr__dot" :style="`background-color: ${item.color}`"></span>

                                <div class="tabsmgr__row-main" @click="startEdit(index)">
                                    <div class="tabsmgr__row-title">{{ item.title }}</div>
                                    <div class="tabsmgr__row-sub">{{ describeTab(item) }}</div>
                                </div>

                                <div class="tabsmgr__row-actions">
                                    <!-- Свіч видимості = поле checked, яким таблиця вирішує, чи малювати таб -->
                                    <div class="form-check form-switch tabsmgr__switch" :title="$t('Visibility')">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="'tabvis_' + index"
                                            v-model="item.checked"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        class="tabsmgr__icon-btn"
                                        :title="$t('Edit')"
                                        @click="startEdit(index)"
                                    >
                                        <i class="bx bx-pencil"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="tabsmgr__icon-btn tabsmgr__icon-btn--danger"
                                        :title="$t('Delete')"
                                        @click="removeTab(index)"
                                    >
                                        <i class="ri-delete-bin-line"></i>
                                    </button>
                                </div>
                            </div>
                        </draggable>
                        <div class="tabsmgr__list-hint">
                            <i class="ri-drag-move-2-line"></i> {{ $t('dragToReorder') }}
                        </div>
                    </template>
                </section>

                <!-- ПРАВА ЗОНА — конструктор вкладки -->
                <section class="tabsmgr__panel tabsmgr__panel--builder">
                    <div class="tabsmgr__panel-head">
                        <div class="tabsmgr__panel-title">
                            {{ editIndex === null ? $t('newTab') : $t('editTab') }}
                        </div>
                        <button
                            v-if="editIndex !== null"
                            type="button"
                            class="btn btn-sm btn-ghost-secondary"
                            @click="resetDraft()"
                        >
                            {{ $t('cancel') }}
                        </button>
                    </div>

                    <!-- Крок 1. Назва + живий прев'ю таба таким, яким він буде в таблиці -->
                    <div class="tabsmgr__field">
                        <label class="tabsmgr__label" for="tabTitleInput">
                            <span class="tabsmgr__step">1</span>{{ $t('Name') }}
                        </label>
                        <input
                            id="tabTitleInput"
                            type="text"
                            class="form-control"
                            :placeholder="$t('Name')"
                            v-model="draft.title"
                            @input="titleTouched = true"
                        />
                        <div class="tabsmgr__preview">
                            <span class="tabsmgr__chip">
                                <i class="tabsmgr__dot" :style="`background-color: ${draft.color}`"></i>
                                {{ draft.title || $t('newTab') }}
                            </span>
                        </div>
                    </div>

                    <!-- Крок 2. Колір — палітра одразу тут, без окремої модалки -->
                    <div class="tabsmgr__field">
                        <label class="tabsmgr__label">
                            <span class="tabsmgr__step">2</span>{{ $t('selectColor') }}
                        </label>
                        <div class="tabsmgr__colors">
                            <button
                                v-for="c in colorsNew"
                                :key="c.color"
                                type="button"
                                class="tabsmgr__swatch"
                                :class="{ 'is-active': draft.color.toUpperCase() === c.color.toUpperCase() }"
                                :style="`background-color: ${c.color}`"
                                :title="c.color"
                                @click="draft.color = c.color"
                            >
                                <i class="ri-check-line"></i>
                            </button>
                            <!-- Свій колір: нативний піклер у вигляді такої ж плитки -->
                            <label class="tabsmgr__swatch tabsmgr__swatch--custom" :title="$t('selectColor')">
                                <i class="bx bx-palette"></i>
                                <input type="color" v-model="draft.color" />
                            </label>
                        </div>
                    </div>

                    <!-- Крок 3. Умови фільтра -->
                    <div class="tabsmgr__field">
                        <label class="tabsmgr__label">
                            <span class="tabsmgr__step">3</span>{{ $t('filter') }}
                        </label>
                        <!-- Логіку зв'язку умов пишемо явно — раніше вона була невидима -->
                        <div class="tabsmgr__cond-hint">{{ $t('AllConditions') }}</div>
                        <div class="tabsmgr__conds">
                            <template v-for="(cond, i) in draft.conditions" :key="i">
                                <div class="tabsmgr__cond">
                                    <select
                                        class="form-select tabsmgr__cond-type"
                                        v-model="cond.obj"
                                        @change="onTypeChange(cond)"
                                    >
                                        <option value="">{{ $t('selectType') }}</option>
                                        <option v-for="typ in listType" :key="typ.value" :value="typ.value">{{ typ.title }}</option>
                                    </select>

                                    <input
                                        v-if="cond.obj === 'search'"
                                        type="text"
                                        class="form-control tabsmgr__cond-value"
                                        :placeholder="$t('searc_inp')"
                                        v-model="cond.value"
                                        @input="cond.title = cond.value; autoName()"
                                    />
                                    <select
                                        v-else
                                        class="form-select tabsmgr__cond-value"
                                        v-model="cond.value"
                                        :disabled="cond.obj === ''"
                                        @change="onValueChange(cond)"
                                    >
                                        <option value="">{{ $t('selectValue') }}</option>
                                        <option v-for="val in valuesFor(cond.obj)" :key="val.value" :value="val.value">{{ val.title }}</option>
                                    </select>

                                    <button
                                        type="button"
                                        class="tabsmgr__icon-btn tabsmgr__icon-btn--danger"
                                        :title="$t('Delete')"
                                        :disabled="draft.conditions.length === 1"
                                        @click="removeCondition(i)"
                                    >
                                        <i class="ri-close-line"></i>
                                    </button>
                                </div>
                            </template>
                        </div>

                        <button
                            type="button"
                            class="btn btn-sm btn-soft-info tabsmgr__add-cond"
                            @click="addCondition()"
                        >
                            <i class="bx bx-plus"></i> {{ $t('Condition') }}
                        </button>
                    </div>

                    <div v-if="error" class="tabsmgr__error">
                        <i class="bx bx-error-circle"></i> {{ error }}
                    </div>

                    <button
                        type="button"
                        class="btn btn-success tabsmgr__submit"
                        :disabled="!isDraftReady"
                        @click="applyDraft()"
                    >
                        <i :class="editIndex === null ? 'bx bx-plus' : 'ri-check-line'"></i>
                        {{ editIndex === null ? $t('Add') : $t('Confirm') }}
                    </button>
                </section>

            </div>
        </div>
        </template>
        <template v-slot:footer-bottom>
            <button type="button" class="btn btn-ghost-secondary" @click="this.$emit('close')">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-success" @click="save">{{ $t('save') }}</button>
        </template>
    </dialogBox>

</template>

<script>
import dialogBox from '@/components/modal/modal-lg'
import { VueDraggableNext } from 'vue-draggable-next'
import { storeS } from '@/store'

export default{
    props: ['othertabs'],
    components: { dialogBox, draggable: VueDraggableNext },
    data(){
        return{
            // Локальна копія табів: правки застосовуються лише на "Зберегти"
            localTabs: [],
            // null — створюємо нову вкладку, число — редагуємо вкладку з таким індексом
            editIndex: null,
            // Чи користувач сам правив назву. Якщо ні — підставляємо назву з умов
            titleTouched: false,
            error: '',
            draft: {
                title: '',
                color: '#1ABC9C',
                conditions: [],
                // tab збереженої вкладки, яку редагуємо (щоб не втратити ідентифікатор)
                originTab: ''
            },
            listType: [
                { title: this.$t('searc_inp'), value: 'search' },
                { title: this.$t('date'), value: 'pr1' },
                { title: this.$t('ticket_type'), value: 'types' },
                { title: this.$t('problem_area'), value: 'problemEnvironments' },
                { title: this.$t('Status'), value: 'statuses' },
                { title: this.$t('decidedStatus'), value: 'decidedStatuses' },
                { title: this.$t('priority'), value: 'priorities' },
                { title: this.$t('added'), value: 'workerIds' },
                { title: this.$t('inJobWorker'), value: 'inJobWorkerIds' },
            ],
            listTypes: [
                { title: this.$t('problem_to_develop'), value: 'problem' },
                { title: this.$t('wish_to_develop'), value: 'wishes' },
            ],
            listValue: [
                { title: this.$t('new'), value: 'new' },
                { title: this.$t('statusInWork'), value: 'jobs' },
                { title: this.$t('statusCompleted'), value: 'decided' },
            ],
            decidedStatusList: [
                { title: this.$t('waitTesting'), value: 'waittesting' },
                { title: this.$t('inTesting'), value: 'intesting' },
                { title: this.$t('waitRelease'), value: 'waitrelease' },
                { title: this.$t('inRelease'), value: 'in_release' },
                { title: this.$t('noProblem'), value: 'noProblem' },
            ],
            listPriority: [
                { title: this.$t('Critical'), value: '3' },
                { title: this.$t('priorityHigh'), value: '2' },
                { title: this.$t('priorityMedium'), value: '1' },
                { title: this.$t('priorityLow'), value: '0' },
            ],
            // Перші 12 кольорів лишились ті самі, що й були — у вже створених табів
            // збережений конкретний hex, і він має підсвічуватись у палітрі
            colorsNew: [
                { color: '#1ABC9C', title: '1ABC9C' },
                { color: '#2ECC71', title: '2ECC71' },
                { color: '#3498DB', title: '3498DB' },
                { color: '#9B59B6', title: '9B59B6' },
                { color: '#34495E', title: '34495E' },
                { color: '#F1C40F', title: 'F1C40F' },
                { color: '#F39C12', title: 'F39C12' },
                { color: '#E74C3C', title: 'E74C3C' },
                { color: '#C2185B', title: 'C2185B' },
                { color: '#BDC3C7', title: 'BDC3C7' },
                { color: '#95A5A6', title: '95A5A6' },
                { color: '#607D8B', title: '607D8B' },
                { color: '#16A085', title: '16A085' },
                { color: '#2980B9', title: '2980B9' },
                { color: '#8E44AD', title: '8E44AD' },
                { color: '#D35400', title: 'D35400' },
            ],
            listDate: [
                { title: this.$t('Today'), value: 'today' },
                { title: this.$t('Yesterday'), value: 'yesterday' }
            ],
            listProblem: [],
            listWorkers: [],
        }
    },
    created(){
        this.changeSelectDevProjects()

        for(var item in this.workers){
            this.listWorkers.push({
                title: this.workers[item].workerName,
                value: this.workers[item].workerId
            })
        }

        // Глибока копія — правки в модалці не чіпають таблицю до збереження
        this.localTabs = this.othertabs ? JSON.parse(JSON.stringify(this.othertabs)) : [];

        this.resetDraft();
    },
    computed: {
        // Кнопка додавання активна лише коли є хоч одна повна умова
        isDraftReady(){
            return this.completeConditions().length > 0
        },
        workers(){
            return storeS.workers
        },
        developmentProjects() {
            // Отримання проектів
            return storeS.developmentProjects
        }
    },
    methods: {
        /* --- список значень для вибраного типу умови --- */
        valuesFor(type){
            switch(type){
                case 'types': return this.listTypes
                case 'pr1': return this.listDate
                case 'statuses': return this.listValue
                case 'decidedStatuses': return this.decidedStatusList
                case 'priorities': return this.listPriority
                case 'problemEnvironments': return this.listProblem
                case 'workerIds':
                case 'inJobWorkerIds': return this.listWorkers
                default: return []
            }
        },
        getTypeTitle(item){
            var title = '';
            this.listType.forEach(element => {
                if(element.value == item){
                    title = element.title
                }
            });
            return title;
        },

        /* --- конструктор --- */
        onTypeChange(cond){
            // Значення від попереднього типу не має сенсу — скидаємо
            cond.value = '';
            cond.title = '';
            this.error = '';
            this.autoName();
        },
        onValueChange(cond){
            var found = this.valuesFor(cond.obj).find(el => el.value === cond.value);
            cond.title = found ? found.title : String(cond.value);
            this.error = '';
            this.autoName();
        },
        addCondition(){
            this.draft.conditions.push({ obj: '', value: '', title: '' });
        },
        removeCondition(i){
            if(this.draft.conditions.length === 1){ return }
            this.draft.conditions.splice(i, 1);
            this.autoName();
        },
        completeConditions(){
            return this.draft.conditions.filter(c => c.obj !== '' && c.value !== '' && c.value !== null && c.value !== undefined)
        },
        // Поки користувач не редагував назву — збираємо її з умов, щоб не змушувати
        // придумувати назву для простої вкладки на одну умову
        autoName(){
            if(this.titleTouched){ return }
            var parts = this.completeConditions().map(c => c.title).filter(t => !!t);
            this.draft.title = parts.join(' + ');
        },
        resetDraft(){
            this.editIndex = null;
            this.titleTouched = false;
            this.error = '';
            this.draft = {
                title: '',
                color: this.freeColor(),
                conditions: [{ obj: '', value: '', title: '' }],
                originTab: ''
            };
        },
        startEdit(index){
            var t = this.localTabs[index];
            this.editIndex = index;
            this.titleTouched = true;
            this.error = '';
            this.draft = {
                title: t.title,
                color: t.color || '#1ABC9C',
                // Мультитаб тримає умови масивом у value, звичайний — одним значенням
                conditions: Array.isArray(t.value)
                    ? t.value.map(v => ({ obj: v.obj, value: v.value, title: v.title }))
                    : [{ obj: t.obj, value: t.value, title: t.title }],
                originTab: t.tab
            };
        },
        // Колір, якого ще немає серед вкладок — щоб таби не зливались
        freeColor(){
            var used = new Set(this.localTabs.map(el => (el.color || '').toUpperCase()));
            var free = this.colorsNew.filter(el => !used.has(el.color.toUpperCase()));
            if(free.length === 0){ return '#1ABC9C' }
            return free[Math.floor(Math.random() * free.length)].color;
        },
        // Збираємо з чернетки об'єкт таба у форматі, який розуміє таблиця:
        // одна умова -> value = значення, кілька умов -> value = масив підфільтрів
        buildTab(){
            var conds = this.completeConditions();
            if(conds.length === 0){ return null }

            if(conds.length === 1){
                var c = conds[0];
                return {
                    title: this.draft.title || c.title,
                    tab: c.obj + c.value,
                    color: this.draft.color,
                    obj: c.obj,
                    value: c.value,
                    hide: true,
                    checked: true
                }
            }

            return {
                title: this.draft.title || conds.map(el => el.title).join(' + '),
                // Раніше всі мультитаби отримували tab: 'tab1' — id збігались, і таблиця
                // не могла їх розрізнити. Тепер id унікальний (або успадкований при правці).
                tab: this.draft.originTab && this.draft.originTab.indexOf('tab1') === 0
                    ? this.draft.originTab
                    : 'tab1_' + Date.now().toString(36),
                color: this.draft.color,
                obj: 'tab1',
                value: conds.map(el => ({
                    title: el.title,
                    tab: el.obj + el.value,
                    obj: el.obj,
                    value: el.value
                })),
                hide: true,
                checked: true
            }
        },
        applyDraft(){
            var built = this.buildTab();
            if(built === null){
                this.error = this.$t('selectValue');
                return
            }

            var dubl = this.localTabs.findIndex((el, i) => el.tab === built.tab && i !== this.editIndex);
            if(dubl !== -1){
                this.error = this.$t('tabAlreadyExists');
                return
            }

            if(this.editIndex === null){
                this.localTabs.push(built);
            } else {
                // Видимість — властивість вкладки, а не чернетки: зберігаємо як було
                built.checked = this.localTabs[this.editIndex].checked !== false;
                this.localTabs.splice(this.editIndex, 1, built);
            }
            this.resetDraft();
        },
        removeTab(i){
            this.localTabs.splice(i, 1);
            // Якщо видалили саме те, що редагували — чернетка більше не має адресата
            if(this.editIndex === i){
                this.resetDraft();
            } else if(this.editIndex !== null && this.editIndex > i){
                this.editIndex = this.editIndex - 1;
            }
        },
        // Підпис під назвою вкладки: з чого вона складається
        describeTab(item){
            if(Array.isArray(item.value)){
                return item.value.map(el => el.title).join(' + ')
            }
            return this.getTypeTitle(item.obj)
        },
        save(){
            /*
                Пропс не мутуємо — новий склад табів віддаємо подією, а батько
                (all.vue > changeTabs) підставляє його собі й пише в localStorage.
            */
            this.$emit('changeTabs', this.localTabs);
            this.$emit('close');
        },
        changeSelectDevProjects(){
            // Робимо вигляд проєктів під мультіселект

            this.devProjects = [];
            for(var item in this.developmentProjects){
                var project = this.developmentProjects[item]
                this.listProblem.push({
                    "title": project.projectName,
                    "value": project.projectKey
                })
            }

        },
    }
}
</script>

<style scoped>
/*
    Уся розмітка скоупнута класом .tabsmgr — модалка ділить стилі з рештою
    застосунку, тож нічого глобального не перевизначаємо.
    Кольори беремо зі змінних теми, щоб світла і темна працювали без дублювання.
*/
.tabsmgr__grid {
    display: grid;
    /* Список вужчий за конструктор: у конструкторі рядки умов з двох селектів */
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: 16px;
    align-items: start;
}
@media (max-width: 991px) {
    .tabsmgr__grid { grid-template-columns: minmax(0, 1fr); }
}

.tabsmgr__panel {
    background: var(--vz-card-bg);
    border: 1px solid var(--vz-border-color);
    border-radius: 10px;
    padding: 14px;
}
.tabsmgr__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    min-height: 30px;
}
.tabsmgr__panel-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--vz-body-color);
    display: flex;
    align-items: center;
    gap: 8px;
}
.tabsmgr__count {
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding: 4px 7px;
    border-radius: 20px;
    color: var(--vz-secondary-color);
    background: rgba(0, 0, 0, 0.06);
}
[data-layout-mode="dark"] .tabsmgr__count { background: rgba(255, 255, 255, 0.09); }
.tabsmgr__new-btn i { font-size: 15px; vertical-align: middle; }

/* --- список вкладок --- */
.tabsmgr__list { display: flex; flex-direction: column; gap: 6px; }
.tabsmgr__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid var(--vz-border-color);
    border-radius: 8px;
    transition: border-color .15s ease, background-color .15s ease;
}
.tabsmgr__row:hover { background: rgba(0, 0, 0, 0.03); }
[data-layout-mode="dark"] .tabsmgr__row:hover { background: rgba(255, 255, 255, 0.05); }
/* Рядок, який зараз відкритий у конструкторі — щоб було видно, що саме правиш */
.tabsmgr__row.is-editing {
    border-color: rgba(10, 179, 156, 0.6);
    background: rgba(10, 179, 156, 0.08);
}
/* Вимкнена вкладка: лишається в списку, але видно, що в таблиці її немає */
.tabsmgr__row.is-off .tabsmgr__row-main,
.tabsmgr__row.is-off .tabsmgr__dot { opacity: .45; }

.tabsmgr__drag {
    cursor: grab;
    font-size: 16px;
    color: var(--vz-secondary-color);
    flex: 0 0 auto;
}
.tabsmgr__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: 0 0 auto;
}
.tabsmgr__row-main { flex: 1 1 auto; min-width: 0; cursor: pointer; }
.tabsmgr__row-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--vz-body-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.tabsmgr__row-sub {
    font-size: 11.5px;
    color: var(--vz-secondary-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.tabsmgr__row-actions { display: flex; align-items: center; gap: 2px; flex: 0 0 auto; }
.tabsmgr__switch { margin: 0; padding: 0; min-height: auto; display: flex; align-items: center; }
.tabsmgr__switch .form-check-input { margin: 0; cursor: pointer; }

.tabsmgr__icon-btn {
    border: none;
    background: transparent;
    color: var(--vz-secondary-color);
    padding: 4px 6px;
    border-radius: 6px;
    font-size: 15px;
    line-height: 1;
    transition: color .15s ease, background-color .15s ease;
}
.tabsmgr__icon-btn:hover { color: var(--vz-body-color); background: rgba(0, 0, 0, 0.06); }
[data-layout-mode="dark"] .tabsmgr__icon-btn:hover { background: rgba(255, 255, 255, 0.09); }
.tabsmgr__icon-btn--danger:hover { color: #f06548; background: rgba(240, 101, 72, 0.12); }
.tabsmgr__icon-btn:disabled { opacity: .35; }
.tabsmgr__icon-btn:disabled:hover { background: transparent; color: var(--vz-secondary-color); }

.tabsmgr__list-hint {
    margin-top: 10px;
    font-size: 11.5px;
    color: var(--vz-secondary-color);
    display: flex;
    align-items: center;
    gap: 5px;
}

.tabsmgr__empty {
    text-align: center;
    padding: 26px 12px;
    border: 1px dashed var(--vz-border-color);
    border-radius: 8px;
}
.tabsmgr__empty i { font-size: 28px; color: var(--vz-secondary-color); }
.tabsmgr__empty-text {
    margin-top: 6px;
    font-size: 12.5px;
    color: var(--vz-secondary-color);
}

/* --- конструктор --- */
.tabsmgr__field { margin-bottom: 16px; }
.tabsmgr__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vz-body-color);
    margin-bottom: 6px;
}
/* Нумерація кроків — головна підказка «що робити спочатку» */
.tabsmgr__step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
    color: #0ab39c;
    background: rgba(10, 179, 156, 0.14);
}

.tabsmgr__preview { margin-top: 8px; }
/* Прев'ю повторює вигляд таба в таблиці: нейтральний текст + крапка кольору */
.tabsmgr__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding: 7px 13px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    color: var(--vz-body-color);
    background: rgba(0, 0, 0, 0.07);
}
[data-layout-mode="dark"] .tabsmgr__chip { background: rgba(255, 255, 255, 0.11); }

.tabsmgr__colors { display: flex; flex-wrap: wrap; gap: 7px; }
.tabsmgr__swatch {
    position: relative;
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: 2px solid transparent;
    padding: 0;
    cursor: pointer;
    transition: transform .12s ease, box-shadow .12s ease;
}
.tabsmgr__swatch:hover { transform: translateY(-1px); }
.tabsmgr__swatch i {
    color: #fff;
    font-size: 15px;
    opacity: 0;
    /* Обводка робить галочку читабельною і на світлих плитках (жовта, сіра) */
    text-shadow: 0 0 3px rgba(0, 0, 0, .55);
}
.tabsmgr__swatch.is-active i { opacity: 1; }
.tabsmgr__swatch.is-active {
    border-color: var(--vz-body-color);
    box-shadow: 0 0 0 2px var(--vz-card-bg) inset;
}
.tabsmgr__swatch--custom {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--vz-border-color);
    margin: 0;
}
.tabsmgr__swatch--custom i { color: var(--vz-secondary-color); opacity: 1; text-shadow: none; }
/* Нативний input[type=color] лишаємо клікабельним, але невидимим поверх плитки */
.tabsmgr__swatch--custom input {
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    border: none;
    padding: 0;
}

.tabsmgr__conds { display: flex; flex-direction: column; gap: 6px; }
.tabsmgr__cond { display: flex; align-items: center; gap: 6px; }
.tabsmgr__cond-type { flex: 0 0 40%; }
.tabsmgr__cond-value { flex: 1 1 auto; min-width: 0; }
/* Правило поєднання умов — один раз над списком, а не підписом між кожним рядком */
.tabsmgr__cond-hint {
    margin: -2px 0 7px 26px;
    font-size: 11.5px;
    color: var(--vz-secondary-color);
}
.tabsmgr__add-cond { margin-top: 10px; }
.tabsmgr__add-cond i { font-size: 15px; vertical-align: middle; }

.tabsmgr__error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding: 8px 10px;
    border-radius: 7px;
    font-size: 12.5px;
    color: #f06548;
    background: rgba(240, 101, 72, 0.12);
}
.tabsmgr__submit { width: 100%; }
.tabsmgr__submit i { font-size: 15px; vertical-align: middle; margin-right: 3px; }
</style>
